import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Typography,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { onSnapshot } from "firebase/firestore";
import { propertyDoc } from "../utils/firestorePaths";
import { useParams, useNavigate } from "react-router-dom";
import PropertyContext from "../contexts/PropertyContext";
import { calculateDaysRemaining, getWarrantyStatus, formatDuration } from "../utils/warrantyUtils";
import AddWarrantyModal from "./AddWarrantyModal";

const WarrantyDetail = ({ clientId: propClientId, selectedProperty: propSelectedProperty, userInfo, readOnly = false }) => {
  const { warrantyId } = useParams();
  const navigate = useNavigate();
  const { selectedProperty: contextProperty, selectedClient: contextClient } = useContext(PropertyContext);
  const selectedProperty = propSelectedProperty || contextProperty;
  const clientId = propClientId || contextClient;

  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Real-time listener for warranty data
  useEffect(() => {
    if (!selectedProperty || !warrantyId) {
      setLoading(false);
      return;
    }

    const warrantyRef = propertyDoc(
      clientId,
      selectedProperty,
      "warranties",
      warrantyId
    );

    const unsubscribe = onSnapshot(
      warrantyRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const daysRemaining = calculateDaysRemaining(data.warrantyEndDate);
          const status = getWarrantyStatus(daysRemaining);

          setWarranty({
            id: docSnapshot.id,
            ...data,
            daysRemaining,
            isActive: daysRemaining >= 0,
            statusLabel: status.label,
            statusColor: status.color,
            chipColor: status.chipColor,
          });
          setLoading(false);
        } else {
          setError("Warranty not found");
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error fetching warranty:", err);
        setError("Failed to load warranty details");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedProperty, warrantyId]);

  const handleBack = () => {
    navigate("/warranty-tracker");
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography>Loading warranty details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !warranty) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error || "Warranty not found"}
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Warranty Tracker
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
          <Typography variant="h4">Warranty Details</Typography>
          <Chip
            label={warranty.statusLabel}
            color={warranty.chipColor}
            size="medium"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!readOnly && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Item Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Item Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Item Name
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {warranty.itemName}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Serial Number
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {warranty.serialNumber}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Item Type / Category
                </Typography>
                <Typography variant="body1">{warranty.itemType}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Vendor
                </Typography>
                <Typography variant="body1">{warranty.vendor || "N/A"}</Typography>
              </Box>

              {warranty.location && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1">{warranty.location}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Purchase Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Purchase Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  PO Number
                </Typography>
                <Typography variant="body1">
                  {warranty.poNumber || "N/A"}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Purchase Date
                </Typography>
                <Typography variant="body1">{warranty.purchaseDate}</Typography>
              </Box>

              {warranty.purchaseCost > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Cost
                  </Typography>
                  <Typography variant="body1">
                    ${warranty.purchaseCost.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Full Width - Warranty Coverage */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Warranty Coverage
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Warranty Type
                    </Typography>
                    <Typography variant="h6">
                      {warranty.warrantyTypeName || "Custom"}
                    </Typography>
                    {warranty.customDurationMonths && (
                      <Typography variant="body2" color="text.secondary">
                        {formatDuration(warranty.customDurationMonths).display}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="h6">
                      {warranty.warrantyStartDate}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="h6">
                      {warranty.warrantyEndDate}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: warranty.isActive ? "success.lighter" : "error.lighter",
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Days Remaining
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: warranty.statusColor,
                        fontWeight: 700,
                      }}
                    >
                      {warranty.daysRemaining < 0
                        ? `${Math.abs(warranty.daysRemaining)} days ago`
                        : `${warranty.daysRemaining} days`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {warranty.statusLabel}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes Section */}
        {warranty.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {warranty.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Audit Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Audit Trail
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body2">
                    {warranty.createdBy || "Unknown"}
                  </Typography>
                  {warranty.createdAt && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(warranty.createdAt?.seconds * 1000).toLocaleString()}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Modified By
                  </Typography>
                  <Typography variant="body2">
                    {warranty.modifiedBy || "Unknown"}
                  </Typography>
                  {warranty.lastModified && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(warranty.lastModified?.seconds * 1000).toLocaleString()}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Modal */}
      <AddWarrantyModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        clientId={clientId}
        selectedProperty={selectedProperty}
        userInfo={userInfo}
        editWarranty={warranty}
        existingWarranties={[]} // Will validate uniqueness excluding current warranty
      />
    </Container>
  );
};

export default WarrantyDetail;
